"use client"
import { Routes, Route, Navigate, useLocation } from "react-router-dom"
import { ThemeProvider } from "@mui/material/styles"
import CssBaseline from "@mui/material/CssBaseline"
import { useAuth } from "./contexts/AuthContext"
import theme from "./theme/theme"
import { UIProvider } from "./contexts/UIContext"
import { AnimatePresence } from "framer-motion"
import { SnackbarProvider } from "notistack"
import { OrganizationProvider } from "./contexts/OrganizationContext"

// Layouts
import MainLayout from "./layouts/MainLayout"
import AuthLayout from "./layouts/AuthLayout"

// Auth Pages
import Login from "./pages/auth/Login"
import ForgotPassword from "./pages/auth/ForgotPassword"
import ResetPassword from "./pages/auth/ResetPassword"

// Import routes
import routes from "./routes"

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { isAuthenticated, user, isLoading } = useAuth()

  // Show loading or redirect while authentication is being checked
  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <div className="pulse">Loading...</div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />
  }

  // Check role permissions only if we have a user and roles to check
  if (allowedRoles.length > 0 && user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" />
  }

  return children
}

// Recursive function to render routes
const renderRoutes = (routes, parentPath = "") => {
  return routes.map((route) => {
    const fullPath = `${parentPath}${route.path}`

    // If the route has children, render them recursively
    if (route.children) {
      return (
        <Route
          key={fullPath}
          path={route.path}
          element={
            route.requiresAuth ? (
              <ProtectedRoute allowedRoles={route.allowedRoles || []}>{route.element}</ProtectedRoute>
            ) : (
              route.element
            )
          }
        >
          {renderRoutes(route.children, `${route.path}/`)}
        </Route>
      )
    }

    // Otherwise, render a simple route
    return (
      <Route
        key={fullPath}
        path={route.path}
        element={
          route.requiresAuth ? (
            <ProtectedRoute allowedRoles={route.allowedRoles || []}>{route.element}</ProtectedRoute>
          ) : (
            route.element
          )
        }
      />
    )
  })
}

const App = () => {
  const { isAuthenticated } = useAuth()
  const location = useLocation()

  // Add auth protection and redirects to routes
  const processedRoutes = routes.map((route) => {
    // Skip auth routes
    if (
      route.path === "/login" ||
      route.path === "/register" ||
      route.path === "/forgot-password" ||
      route.path.startsWith("/reset-password")
    ) {
      return {
        ...route,
        element: isAuthenticated ? <Navigate to="/dashboard" /> : route.element,
      }
    }

    // Add auth protection to other routes
    if (route.path !== "*") {
      return {
        ...route,
        requiresAuth: true,
      }
    }

    return route
  })

  // Check if current route is an auth page
  const isAuthPage =
    location.pathname === "/login" ||
    location.pathname === "/register" ||
    location.pathname === "/forgot-password" ||
    location.pathname.startsWith("/reset-password")

  return (
    <ThemeProvider theme={theme}>
      <SnackbarProvider maxSnack={3} autoHideDuration={3000}>
        {isAuthPage ? (
          <UIProvider>
            <CssBaseline />
            <AnimatePresence mode="wait">
              <Routes location={location} key={location.pathname}>
                {/* Auth Layout */}
                <Route path="/" element={<AuthLayout />}>
                  <Route
                    index
                    element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />}
                  />
                  <Route path="login" element={<Login />} />
                  <Route path="forgot-password" element={<ForgotPassword />} />
                  <Route path="reset-password/:token" element={<ResetPassword />} />
                </Route>
                {/* Catch-all route for auth pages */}
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
            </AnimatePresence>
          </UIProvider>
        ) : (
          <OrganizationProvider>
            <UIProvider>
              <CssBaseline />
              <AnimatePresence mode="wait">
                <Routes location={location} key={location.pathname}>
                  {/* Redirects for common paths */}
                  <Route path="/profile" element={<Navigate to="/settings/profile" replace />} />
                  <Route path="/organization" element={<Navigate to="/settings/organization" replace />} />
                  <Route path="/email-templates" element={<Navigate to="/settings/email-templates" replace />} />
                  <Route path="/payment-recovery" element={<Navigate to="/settings/payment-recovery" replace />} />

                  {/* Auth Layout */}
                  <Route path="/" element={<AuthLayout />}>
                    <Route
                      index
                      element={
                        isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
                      }
                    />
                    <Route path="login" element={<Login />} />
                    <Route path="forgot-password" element={<ForgotPassword />} />
                    <Route path="reset-password/:token" element={<ResetPassword />} />
                  </Route>

                  {/* Main Layout */}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <MainLayout />
                      </ProtectedRoute>
                    }
                  >
                    {renderRoutes(processedRoutes)}
                  </Route>
                </Routes>
              </AnimatePresence>
            </UIProvider>
          </OrganizationProvider>
        )}
      </SnackbarProvider>
    </ThemeProvider>
  )
}

export default App

